import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  Select,
  Spinner,
  Text,
  Textarea,
  VStack,
  useColorModeValue,
} from "@chakra-ui/react";
import { FiCheckCircle } from "react-icons/fi";

/**
 * One chat conversation: messages, a composer, and a "Move chat to…" selector
 * that reassigns the chat's category (subject/topic).
 *
 * @param {{
 *   chat: object|null,
 *   tree: Array,
 *   sending: boolean,
 *   onSend: (text: string) => void,
 *   onMoveCategory: (categoryId: string) => void,
 *   canChat: boolean,
 *   onBuyCredits: () => void,
 * }} props
 */
export default function ExamChatPanel({
  chat,
  tree = [],
  sending = false,
  onSend,
  onMoveCategory,
  canChat = true,
  onBuyCredits,
}) {
  const [text, setText] = useState("");
  const scrollRef = useRef(null);

  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const userBubble = useColorModeValue("purple.500", "purple.400");
  const aiBubble = useColorModeValue("gray.100", "gray.700");
  const aiText = useColorModeValue("gray.800", "gray.100");
  const muted = useColorModeValue("gray.600", "gray.400");

  const messages = chat?.messages || [];

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length, sending]);

  const handleSend = () => {
    const t = text.trim();
    if (!t) return;
    if (!canChat) {
      onBuyCredits?.();
      return;
    }
    onSend(t);
    setText("");
  };

  // Flattened, indented category options (any nesting depth) for the move selector.
  const options = [];
  const walk = (nodes, depth) => {
    (nodes || []).forEach((n) => {
      options.push({
        id: String(n._id),
        label: `${depth ? "— ".repeat(depth) : ""}${n.name}`,
      });
      walk(n.children || [], depth + 1);
    });
  };
  walk(tree, 0);

  if (!chat) {
    return (
      <Flex flex="1" align="center" justify="center" p={8}>
        <Text color={muted} fontSize="sm" textAlign="center" lineHeight="tall">
          Pick a subject or topic on the left and start a chat to study with your
          AI tutor.
        </Text>
      </Flex>
    );
  }

  return (
    <Flex direction="column" flex="1" minH={0}>
      <HStack
        px={4}
        py={3}
        borderBottomWidth="1px"
        borderColor={borderColor}
        spacing={3}
        flexWrap="wrap"
      >
        <Text fontSize="sm" fontWeight="semibold" noOfLines={1} flex="1" minW="120px">
          {chat.title || "Chat"}
        </Text>
        <HStack spacing={2}>
          <Text fontSize="xs" color={muted} whiteSpace="nowrap">
            Move to
          </Text>
          <Select
            size="xs"
            maxW="220px"
            value={String(chat.categoryId)}
            onChange={(e) => onMoveCategory(e.target.value)}
          >
            {options.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </Select>
        </HStack>
      </HStack>

      <Box ref={scrollRef} flex="1" overflowY="auto" px={4} py={4} minH="240px">
        {messages.length === 0 ? (
          <Text color={muted} fontSize="sm" textAlign="center" mt={6}>
            Ask your first question about this topic.
          </Text>
        ) : (
          <VStack align="stretch" spacing={3}>
            {messages.map((m) => (
              <Flex
                key={String(m._id)}
                justify={m.role === "user" ? "flex-end" : "flex-start"}
              >
                <Box maxW="85%">
                  <Box
                    px={4}
                    py={2}
                    borderRadius="lg"
                    bg={m.role === "user" ? userBubble : aiBubble}
                    color={m.role === "user" ? "white" : aiText}
                    whiteSpace="pre-wrap"
                    fontSize="sm"
                    lineHeight="tall"
                  >
                    {m.content}
                  </Box>
                  {m.role === "assistant" && m.verified ? (
                    <HStack spacing={1} mt={1} pl={1}>
                      <Icon as={FiCheckCircle} color="green.500" boxSize={3} />
                      <Text fontSize="2xs" color={muted}>
                        AI fact-checked
                      </Text>
                    </HStack>
                  ) : null}
                </Box>
              </Flex>
            ))}
            {sending ? (
              <Flex justify="flex-start">
                <HStack
                  px={4}
                  py={2}
                  borderRadius="lg"
                  bg={aiBubble}
                  color={aiText}
                  spacing={2}
                >
                  <Spinner size="xs" />
                  <Text fontSize="sm">Thinking & fact-checking…</Text>
                </HStack>
              </Flex>
            ) : null}
          </VStack>
        )}
      </Box>

      <Box
        p={3}
        borderTopWidth="1px"
        borderColor={borderColor}
        bg={cardBg}
      >
        <HStack align="end" spacing={2}>
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Ask a question about this topic…"
            size="sm"
            rows={2}
            resize="none"
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
          />
          <Button
            colorScheme="purple"
            size="sm"
            onClick={handleSend}
            isLoading={sending}
            isDisabled={!text.trim()}
          >
            Send
          </Button>
        </HStack>
      </Box>
    </Flex>
  );
}
